import { VideoStatus } from "../__generated__/graphql";

export const isVideoReady = (s?: VideoStatus | null) => s === VideoStatus.Ready;
export const isVideoProcessing = (s?: VideoStatus | null) => s === VideoStatus.Processing;
export const isVideoFailed = (s?: VideoStatus | null) => s === VideoStatus.Failed;

export type VideoInputState =
  | "empty"
  | "ready"
  | "failed"
  | "unavailable"
  | "stalled"
  | "processing";

// How long a video may sit in processing before we stop claiming it's on track. We keep
// checking past this point — it changes what we say, not what we do.
export const PROCESSING_TIMEOUT_MS = 5 * 60 * 1000;

// The render state of a video upload field, derived from the VideoById response.
//
// Readiness comes from `status` alone — a missing thumbnail is a rendering detail, not a
// reason to keep spinning. A transient query error says nothing about the transcode, so it
// is deliberately NOT an input here: the caller keeps polling through it and recovers on
// its own. `isMissing` is different — it means the server successfully answered and had no
// such video, which no amount of polling will change.
export const videoInputState = ({
  videoId,
  status,
  isMissing = false,
  hasStalled = false,
}: {
  videoId?: string | null;
  status?: VideoStatus | null;
  isMissing?: boolean;
  hasStalled?: boolean;
}): VideoInputState => {
  if (!videoId) return "empty";
  // A resolved video outranks the clock, so one that finishes just past the ceiling
  // still renders as ready rather than stalled.
  if (isVideoReady(status)) return "ready";
  if (isVideoFailed(status)) return "failed";
  if (isMissing) return "unavailable";
  if (hasStalled) return "stalled";
  return "processing";
};

// Only the terminal states stop polling. `stalled` keeps checking — it exists to stop us
// promising the video is on track, not to stop us noticing when it arrives.
export const shouldPollVideo = (state: VideoInputState) =>
  state === "processing" || state === "stalled";

// Has the wait run longer than the ceiling? Callers should anchor this to the server's
// createdAt where possible, so the clock survives a remount and measures the real
// transcode. A numeric anchor is accepted for the fallback case: when the query keeps
// failing we never learn createdAt, and without some anchor the wait could never resolve.
export const hasProcessingStalled = (
  startedAt?: string | number | null,
  now: number = Date.now()
): boolean => {
  if (startedAt === null || startedAt === undefined) return false;
  const started =
    typeof startedAt === "number" ? startedAt : new Date(startedAt).getTime();
  if (Number.isNaN(started)) return false;
  return now - started > PROCESSING_TIMEOUT_MS;
};

// Display info for the teaser list cell. `tone` maps to the badge/Tag styling.
export const videoStatusDisplay = (
  s?: VideoStatus | null
): { label: string; tone: "success" | "processing" | "error" } | null => {
  if (isVideoReady(s)) return { label: "Ready", tone: "success" };
  if (isVideoFailed(s)) return { label: "Video upload failed", tone: "error" };
  if (isVideoProcessing(s)) return { label: "Processing", tone: "processing" };
  return null;
};
